import { useEffect, useState, useRef } from "react";
import { motion, useMotionValue, useTransform, type PanInfo, type Transition } from "motion/react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

import "@/components/ui/react-bits/Carousel.css";
import Image from "next/image";
import { JumbotronItem } from "@/lib/types/shop";
import { cn } from "@/lib/utils";
import Link from "next/link";

const DRAG_BUFFER = 0;
const VELOCITY_THRESHOLD = 500;
const GAP = 16;
const SPRING_OPTIONS: Transition = { type: "spring", stiffness: 300, damping: 30 };

export default function ShopPageJumbotron({
  items,
  baseWidth,
  autoplay = false,
  autoplayDelay = 3000,
  pauseOnHover = false,
  loop = false,
  round = false,
}: {
  items: JumbotronItem[];
  baseWidth?: number;
  autoplay?: boolean;
  autoplayDelay?: number;
  pauseOnHover?: boolean;
  loop?: boolean;
  round?: boolean;
}) {
  const containerPadding = 16;
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(baseWidth || 0);

  // Measure container dimensions on mount and resize
  useEffect(() => {
    if (!containerRef.current) return;

    const updateDimensions = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setContainerWidth(width);
      }
    };

    // Set initial width
    updateDimensions();

    // Create ResizeObserver to watch for container size changes
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [baseWidth]);

  // Use baseWidth if provided, otherwise use measured container width
  const effectiveWidth = baseWidth || containerWidth;
  const itemWidth = effectiveWidth > 0 ? effectiveWidth - containerPadding * 2 : 0;
  const trackItemOffset = itemWidth + GAP;

  const carouselItems = loop ? [...items, items[0]] : items;
  const [currentIndex, setCurrentIndex] = useState(0);
  const x = useMotionValue(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  // Create transforms for up to 20 items at the top level to ensure consistent hook order
  // These will be used in the map below - we always create the same number of hooks
  // Always call useTransform unconditionally - the ranges will be recalculated when trackItemOffset changes
  const rotateY0 = useTransform(x, [-1 * trackItemOffset, 0, 1 * trackItemOffset], [90, 0, -90], { clamp: false });
  const rotateY1 = useTransform(x, [-2 * trackItemOffset, -1 * trackItemOffset, 0], [90, 0, -90], { clamp: false });
  const rotateY2 = useTransform(x, [-3 * trackItemOffset, -2 * trackItemOffset, -1 * trackItemOffset], [90, 0, -90], {
    clamp: false,
  });
  const rotateY3 = useTransform(x, [-4 * trackItemOffset, -3 * trackItemOffset, -2 * trackItemOffset], [90, 0, -90], {
    clamp: false,
  });
  const rotateY4 = useTransform(x, [-5 * trackItemOffset, -4 * trackItemOffset, -3 * trackItemOffset], [90, 0, -90], {
    clamp: false,
  });
  const rotateY5 = useTransform(x, [-6 * trackItemOffset, -5 * trackItemOffset, -4 * trackItemOffset], [90, 0, -90], {
    clamp: false,
  });
  const rotateY6 = useTransform(x, [-7 * trackItemOffset, -6 * trackItemOffset, -5 * trackItemOffset], [90, 0, -90], {
    clamp: false,
  });
  const rotateY7 = useTransform(x, [-8 * trackItemOffset, -7 * trackItemOffset, -6 * trackItemOffset], [90, 0, -90], {
    clamp: false,
  });
  const rotateY8 = useTransform(x, [-9 * trackItemOffset, -8 * trackItemOffset, -7 * trackItemOffset], [90, 0, -90], {
    clamp: false,
  });
  const rotateY9 = useTransform(x, [-10 * trackItemOffset, -9 * trackItemOffset, -8 * trackItemOffset], [90, 0, -90], {
    clamp: false,
  });
  const rotateY10 = useTransform(
    x,
    [-11 * trackItemOffset, -10 * trackItemOffset, -9 * trackItemOffset],
    [90, 0, -90],
    { clamp: false }
  );
  const rotateY11 = useTransform(
    x,
    [-12 * trackItemOffset, -11 * trackItemOffset, -10 * trackItemOffset],
    [90, 0, -90],
    { clamp: false }
  );
  const rotateY12 = useTransform(
    x,
    [-13 * trackItemOffset, -12 * trackItemOffset, -11 * trackItemOffset],
    [90, 0, -90],
    { clamp: false }
  );
  const rotateY13 = useTransform(
    x,
    [-14 * trackItemOffset, -13 * trackItemOffset, -12 * trackItemOffset],
    [90, 0, -90],
    { clamp: false }
  );
  const rotateY14 = useTransform(
    x,
    [-15 * trackItemOffset, -14 * trackItemOffset, -13 * trackItemOffset],
    [90, 0, -90],
    { clamp: false }
  );
  const rotateY15 = useTransform(
    x,
    [-16 * trackItemOffset, -15 * trackItemOffset, -14 * trackItemOffset],
    [90, 0, -90],
    { clamp: false }
  );
  const rotateY16 = useTransform(
    x,
    [-17 * trackItemOffset, -16 * trackItemOffset, -15 * trackItemOffset],
    [90, 0, -90],
    { clamp: false }
  );
  const rotateY17 = useTransform(
    x,
    [-18 * trackItemOffset, -17 * trackItemOffset, -16 * trackItemOffset],
    [90, 0, -90],
    { clamp: false }
  );
  const rotateY18 = useTransform(
    x,
    [-19 * trackItemOffset, -18 * trackItemOffset, -17 * trackItemOffset],
    [90, 0, -90],
    { clamp: false }
  );
  const rotateY19 = useTransform(
    x,
    [-20 * trackItemOffset, -19 * trackItemOffset, -18 * trackItemOffset],
    [90, 0, -90],
    { clamp: false }
  );

  const rotateYTransforms = [
    rotateY0,
    rotateY1,
    rotateY2,
    rotateY3,
    rotateY4,
    rotateY5,
    rotateY6,
    rotateY7,
    rotateY8,
    rotateY9,
    rotateY10,
    rotateY11,
    rotateY12,
    rotateY13,
    rotateY14,
    rotateY15,
    rotateY16,
    rotateY17,
    rotateY18,
    rotateY19,
  ];

  useEffect(() => {
    if (pauseOnHover && containerRef.current) {
      const container = containerRef.current;
      const handleMouseEnter = () => setIsHovered(true);
      const handleMouseLeave = () => setIsHovered(false);
      container.addEventListener("mouseenter", handleMouseEnter);
      container.addEventListener("mouseleave", handleMouseLeave);
      return () => {
        container.removeEventListener("mouseenter", handleMouseEnter);
        container.removeEventListener("mouseleave", handleMouseLeave);
      };
    }
  }, [pauseOnHover]);

  useEffect(() => {
    if (autoplay && (!pauseOnHover || !isHovered)) {
      const timer = setInterval(() => {
        setCurrentIndex((prev) => {
          if (prev === items.length - 1 && loop) {
            return prev + 1;
          }
          if (prev === carouselItems.length - 1) {
            return loop ? 0 : prev;
          }
          return prev + 1;
        });
      }, autoplayDelay);
      return () => clearInterval(timer);
    }
  }, [autoplay, autoplayDelay, isHovered, loop, items.length, carouselItems.length, pauseOnHover]);

  const effectiveTransition: Transition = isResetting ? { duration: 0 } : SPRING_OPTIONS;

  const handleAnimationComplete = () => {
    if (loop && currentIndex === carouselItems.length - 1) {
      setIsResetting(true);
      x.set(0);
      setCurrentIndex(0);
      setTimeout(() => setIsResetting(false), 50);
    }
  };

  const handleDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const offset = info.offset.x;
    const velocity = info.velocity.x;
    if (offset < -DRAG_BUFFER || velocity < -VELOCITY_THRESHOLD) {
      if (loop && currentIndex === items.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        setCurrentIndex((prev) => Math.min(prev + 1, carouselItems.length - 1));
      }
    } else if (offset > DRAG_BUFFER || velocity > VELOCITY_THRESHOLD) {
      if (loop && currentIndex === 0) {
        setCurrentIndex(items.length - 1);
      } else {
        setCurrentIndex((prev) => Math.max(prev - 1, 0));
      }
    }
  };

  const handlePrevious = () => {
    if (loop) {
      if (currentIndex === 0) {
        setCurrentIndex(items.length - 1);
      } else {
        setCurrentIndex((prev) => prev - 1);
      }
    } else {
      setCurrentIndex((prev) => Math.max(prev - 1, 0));
    }
  };

  const handleNext = () => {
    if (loop) {
      if (currentIndex === items.length - 1) {
        setCurrentIndex(currentIndex + 1);
      } else if (currentIndex === carouselItems.length - 1) {
        setCurrentIndex(0);
      } else {
        setCurrentIndex((prev) => prev + 1);
      }
    } else {
      setCurrentIndex((prev) => Math.min(prev + 1, carouselItems.length - 1));
    }
  };

  const canGoPrevious = loop || currentIndex > 0;
  const canGoNext = loop || currentIndex < carouselItems.length - 1;

  const dragProps = loop
    ? {}
    : {
        dragConstraints: {
          left: -trackItemOffset * (carouselItems.length - 1),
          right: 0,
        },
      };

  return (
    <div
      ref={containerRef}
      className={`carousel-container relative h-full w-full overflow-hidden border-0 ${round ? "round" : ""}`}
      style={{
        ...(baseWidth && { width: `${baseWidth}px` }),
        ...(round && baseWidth && { height: `${baseWidth}px`, borderRadius: "50%" }),
      }}
    >
      {itemWidth > 0 && (
        <motion.div
          className='carousel-track'
          drag='x'
          {...dragProps}
          style={{
            width: itemWidth,
            height: "100%",
            alignItems: "stretch",
            gap: `${GAP}px`,
            perspective: 1000,
            perspectiveOrigin: `${currentIndex * trackItemOffset + itemWidth / 2}px 50%`,
            x,
          }}
          onDragEnd={handleDragEnd}
          animate={{ x: -(currentIndex * trackItemOffset) }}
          transition={effectiveTransition}
          onAnimationComplete={handleAnimationComplete}
        >
          {carouselItems.map((item, index) => {
            // Use the pre-created transform from the array (only use if index is within bounds)
            const rotateY = rotateYTransforms[index] || rotateYTransforms[0];
            return (
              <motion.div
                key={index}
                className={`carousel-item ${round ? "round" : ""}`}
                style={{
                  width: itemWidth,
                  height: round ? itemWidth : "100%",
                  minHeight: round ? itemWidth : "100%",
                  flexShrink: 0,
                  rotateY: rotateY,
                  ...(round && { borderRadius: "50%" }),
                }}
                transition={effectiveTransition}
              >
                {item.image && (
                  <Link href={item.productLink} className={`absolute inset-0 ${round ? "round" : ""}`}>
                    <Image src={item.image} alt={`Item ${item.id}`} className='object-cover' fill sizes='100vw' />
                  </Link>
                )}
              </motion.div>
            );
          })}
        </motion.div>
      )}
      {/* Left Chevron Button */}
      <button
        onClick={handlePrevious}
        disabled={!canGoPrevious}
        className='absolute top-1/2 left-8 z-20 -translate-y-1/2 cursor-pointer rounded-full bg-white p-2 transition-all hover:bg-white/80 focus:ring-2 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white/50'
        aria-label='Previous slide'
      >
        <FiChevronLeft className='text-icon-title dark:text-icon-title-inv size-4' />
      </button>
      {/* Right Chevron Button */}
      <button
        onClick={handleNext}
        disabled={!canGoNext}
        className='absolute top-1/2 right-8 z-20 -translate-y-1/2 cursor-pointer rounded-full bg-white p-2 transition-all hover:bg-white/80 focus:ring-2 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white/50'
        aria-label='Next slide'
      >
        <FiChevronRight className='text-icon-title dark:text-icon-title-inv size-4' />
      </button>
      {/* Dots Indicator */}
      <div className={`absolute inset-x-0 bottom-6 z-20 flex justify-center gap-2 px-8 ${round ? "round" : ""}`}>
        {items.map((_, index) => {
          const isActive = currentIndex % items.length === index;
          return (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={cn(
                "h-2 rounded-full transition-all",
                isActive ? "bg-link-primary w-8" : "w-2 bg-white hover:bg-white/80"
              )}
              aria-label={`Go to slide ${index + 1}`}
            />
          );
        })}
      </div>
    </div>
  );
}
